/**
 * Offline service worker registration and updates (production builds only).
 *
 * A new deploy reaches returning users without manual steps:
 * - The browser checks sw.js on every navigation; we also ask it to check whenever the page comes
 *   back to the foreground and every hour, so a tab or installed app left open picks up a deploy.
 *   (That request only fetches the app's own sw.js, i.e. loading the static app itself.)
 * - The new worker skips waiting and claims open pages (sw-template.js), which fires
 *   `controllerchange` here.
 * - Then: nothing entered yet → reload at once onto the new version. Something entered (a reload
 *   would wipe it, since payslip data lives only in page memory) → `showNotice`, a one-tap banner.
 * - "Check for updates" in the footer (`checkForUpdates`) runs the same check at once and says what
 *   it found; a newer build then takes over and reloads the page the same way.
 */
import { isNewerBuild } from '../build/version';

export interface UpdateHooks {
  /** True when a reload would lose something the user entered or loaded. */
  hasUnsavedData(): boolean;
  showNotice(): void;
  reload(): void;
}

/** The browser objects used, injectable for tests. */
export interface UpdateEnv {
  serviceWorker: Pick<ServiceWorkerContainer, 'controller' | 'addEventListener' | 'register'>;
  document: Pick<Document, 'visibilityState' | 'addEventListener'>;
  window: Pick<Window, 'addEventListener' | 'setInterval'>;
  /** The build this page runs (src/build/buildId.ts). */
  buildId?: string;
  /** The published /version.json, fetched fresh; null when offline or unavailable (`askVersion`). */
  askVersion?: () => Promise<{ buildId: string } | null>;
  /** Remembers a reload made for a published build (sessionStorage), so it can't loop. */
  session?: Pick<Storage, 'getItem' | 'setItem'>;
}

export const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

/**
 * What a check the user asked for found: `found` = a newer build is installing (the page reloads when
 * it takes over, or shows the banner if something was entered); `latest` = this is the newest build;
 * `offline` = the check failed; `unavailable` = no service worker (a dev build, or a browser without).
 */
export type UpdateCheck = 'found' | 'latest' | 'offline' | 'unavailable';

type Registration = Pick<ServiceWorkerRegistration, 'update' | 'installing' | 'waiting'>;

/** Starts watching; returns the check behind "Check for updates". */
export function watchForUpdates(hooks: UpdateHooks, env: UpdateEnv): () => Promise<UpdateCheck> {
  const { serviceWorker } = env;
  // On a first visit there is no controller yet; taking control then is not an update (unless the
  // user asked for one).
  const hadController = serviceWorker.controller !== null;
  let userAsked = false;
  let reloading = false;
  let resolveRegistered: (r: Registration | undefined) => void = () => undefined;
  const registered = new Promise<Registration | undefined>((resolve) => (resolveRegistered = resolve));
  const moveToNewVersion = () => {
    if (hooks.hasUnsavedData()) {
      hooks.showNotice();
    } else {
      reloading = true;
      hooks.reload();
    }
  };
  serviceWorker.addEventListener('controllerchange', () => {
    if ((!hadController && !userAsked) || reloading) return;
    moveToNewVersion();
  });
  env.window.addEventListener('load', () => {
    serviceWorker.register('./sw.js').then(
      (registration) => {
        resolveRegistered(registration);
        // A failed check (offline) is harmless: the next one tries again.
        const check = () => void registration.update().catch(() => undefined);
        // The published version (on every open and back in the foreground): a newer build than this page
        // runs → fetch its service worker now; when it takes over, the controllerchange handler above
        // reloads (or shows the banner when something was entered). Offline: skipped quietly.
        const checkVersion = async () => {
          const published = await env.askVersion?.().catch(() => null);
          if (!published || !env.buildId || !isNewerBuild(published.buildId, env.buildId)) return;
          userAsked = true;
          await registration.update().catch(() => undefined);
          if (registration.installing || registration.waiting || reloading) return;
          // Nothing left to install: the newest worker already controls this page (it took over while the
          // page was loading, before the controllerchange listener existed), so the page itself is the old
          // build. Move to the new one, once per published build (a reload that lands on the same old build
          // shows the banner instead of looping).
          const key = `magia-li.reloadedFor.${published.buildId}`;
          if (env.session?.getItem(key)) return hooks.showNotice();
          env.session?.setItem(key, '1');
          moveToNewVersion();
        };
        void checkVersion();
        env.document.addEventListener('visibilitychange', () => {
          if (env.document.visibilityState !== 'visible') return;
          check();
          void checkVersion();
        });
        env.window.setInterval(check, UPDATE_CHECK_INTERVAL_MS);
      },
      (err: unknown) => {
        resolveRegistered(undefined);
        console.warn('Offline mode unavailable:', err);
      },
    );
  });
  return async () => {
    const registration = await registered;
    if (!registration) return 'unavailable';
    try {
      await registration.update();
    } catch {
      return 'offline';
    }
    if (!registration.installing && !registration.waiting) return 'latest';
    userAsked = true;
    return 'found';
  };
}

let check: (() => Promise<UpdateCheck>) | undefined;

/** "Check for updates": asks for the app's sw.js now (loading the static app itself, nothing else). */
export const checkForUpdates = (): Promise<UpdateCheck> => (check ? check() : Promise.resolve('unavailable'));

export const VERSION_TIMEOUT_MS = 10_000;

/**
 * /version.json through the service worker: the page's CSP allows it no requests (connect-src 'none'),
 * the service worker fetches the app's own files anyway. Fresh from the server (cache: 'no-store');
 * null when offline, when no worker controls the page yet, or after the timeout.
 */
export function askVersionViaWorker(container: Pick<ServiceWorkerContainer, 'controller'> = navigator.serviceWorker, ms = VERSION_TIMEOUT_MS): Promise<{ buildId: string } | null> {
  const worker = container.controller;
  if (!worker) return Promise.resolve(null);
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const timer = setTimeout(() => resolve(null), ms);
    channel.port1.onmessage = (e: MessageEvent<unknown>) => {
      clearTimeout(timer);
      const v = e.data as { buildId?: unknown } | null;
      resolve(v && typeof v.buildId === 'string' ? { buildId: v.buildId } : null);
    };
    worker.postMessage({ type: 'checkVersion' }, [channel.port2]);
  });
}

export function registerServiceWorker(hooks: UpdateHooks, buildId?: string): void {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  let session: Pick<Storage, 'getItem' | 'setItem'> | undefined;
  try {
    session = sessionStorage;
  } catch {
    session = undefined;
  }
  check = watchForUpdates(hooks, { serviceWorker: navigator.serviceWorker, document, window, ...(buildId ? { buildId } : {}), ...(session ? { session } : {}), askVersion: () => askVersionViaWorker() });
}

